import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Progress } from './ui/progress';
import { useToast } from '../hooks/use-toast';
import { 
  BarChart3, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, 
  Target, Activity, Shield, Users, Calendar, RefreshCw, Download,
  Award, Gauge, PieChart, ArrowUp, ArrowDown, Minus, FileText,
  Building2, Clipboard, AlertCircle, ThumbsUp
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SafetyAnalyticsPage = () => {
  const [dashboard, setDashboard] = useState(null);
  const [incidentTrends, setIncidentTrends] = useState([]);
  const [monthlyTrends, setMonthlyTrends] = useState([]);
  const [leadingIndicators, setLeadingIndicators] = useState(null);
  const [laggingIndicators, setLaggingIndicators] = useState(null);
  const [riskScore, setRiskScore] = useState(null);
  const [cultureIndex, setCultureIndex] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [kpis, setKpis] = useState([]);
  const [departmentComparison, setDepartmentComparison] = useState([]);
  const [hazardCategories, setHazardCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const { toast } = useToast();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [
        dashRes, trendsRes, monthlyRes, leadingRes, laggingRes, 
        riskRes, cultureRes, complianceRes, kpiRes, deptRes, hazRes
      ] = await Promise.all([
        axios.get(`${API}/api/safety-analytics/dashboard?days=${period}`),
        axios.get(`${API}/api/safety-analytics/trends/incidents?days=${period}`),
        axios.get(`${API}/api/safety-analytics/monthly-trends?months=6`).catch(() => ({ data: { monthly_trends: [] } })),
        axios.get(`${API}/api/safety-analytics/leading-indicators?days=${period}`),
        axios.get(`${API}/api/safety-analytics/lagging-indicators`),
        axios.get(`${API}/api/safety-analytics/risk-score`),
        axios.get(`${API}/api/safety-analytics/safety-culture-index`).catch(() => ({ data: null })),
        axios.get(`${API}/api/safety-analytics/compliance-summary`).catch(() => ({ data: null })),
        axios.get(`${API}/api/safety-analytics/kpi-summary`).catch(() => ({ data: { kpis: [] } })),
        axios.get(`${API}/api/safety-analytics/department-comparison?days=${period}`),
        axios.get(`${API}/api/safety-analytics/hazard-categories?days=${period}`)
      ]);
      setDashboard(dashRes.data);
      setIncidentTrends(trendsRes.data.trends || []);
      setMonthlyTrends(monthlyRes.data.monthly_trends || []);
      setLeadingIndicators(leadingRes.data);
      setLaggingIndicators(laggingRes.data);
      setRiskScore(riskRes.data);
      setCultureIndex(cultureRes.data);
      setCompliance(complianceRes.data);
      setKpis(kpiRes.data.kpis || []);
      setDepartmentComparison(deptRes.data.departments || []);
      setHazardCategories(hazRes.data.categories || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load analytics', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [period, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const exportReport = async () => {
    try {
      const response = await axios.get(`${API}/api/safety-analytics/export/report?days=${period}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Safety_Analytics_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast({ title: 'Success', description: 'Report downloaded successfully' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to export report', variant: 'destructive' });
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'critical': return 'text-red-600 bg-red-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'moderate': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getScoreColor = (score, target) => {
    const ratio = score / target;
    if (ratio >= 1) return 'bg-green-500';
    if (ratio >= 0.7) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getGradeColor = (grade) => {
    switch (grade) {
      case 'A': return 'text-green-600 bg-green-100';
      case 'B': return 'text-blue-600 bg-blue-100';
      case 'C': return 'text-yellow-600 bg-yellow-100';
      case 'D': return 'text-orange-600 bg-orange-100';
      case 'F': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getComplianceColor = (status) => {
    switch (status) {
      case 'compliant': return 'bg-green-100 text-green-800 border-green-300';
      case 'warning': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'non_compliant': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getKPIStatus = (kpi) => {
    if (kpi.good_direction === 'higher') {
      return kpi.value >= kpi.target;
    }
    return kpi.value <= kpi.target;
  };

  // Simple bar chart component
  const SimpleBarChart = ({ data, dataKey, maxValue, color = 'bg-blue-500' }) => {
    const max = maxValue || Math.max(...data.map(d => d[dataKey]), 1);
    return (
      <div className="flex items-end gap-1 h-32">
        {data.map((item, idx) => (
          <div key={idx} className="flex-1 flex flex-col items-center">
            <div 
              className={`w-full ${color} rounded-t transition-all`}
              style={{ height: `${(item[dataKey] / max) * 100}%`, minHeight: item[dataKey] > 0 ? '4px' : '0' }}
              title={`${item.month}: ${item[dataKey]}`}
            />
            <span className="text-xs text-gray-500 mt-1 truncate w-full text-center">{item.month?.split(' ')[0]}</span>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Safety Analytics Dashboard</h1>
          <p className="text-gray-600">Comprehensive safety performance insights</p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last Year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button onClick={exportReport}>
            <Download className="w-4 h-4 mr-2" /> Export PDF
          </Button>
        </div>
      </div>

      {/* Top Row - Risk Score & Culture Index */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Predictive Risk Score */}
        {riskScore && (
          <Card className={`border-2 ${riskScore.risk_level === 'low' ? 'border-green-300' : riskScore.risk_level === 'moderate' ? 'border-yellow-300' : riskScore.risk_level === 'high' ? 'border-orange-300' : 'border-red-300'}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Gauge className="w-5 h-5 text-blue-600" />
                    <h3 className="text-lg font-semibold">Predictive Risk Score</h3>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">{riskScore.recommendation}</p>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Risk Factors</p>
                      <p className="font-medium">
                        {riskScore.factors?.risk_factors?.recent_incidents || 0} incidents, 
                        {riskScore.factors?.risk_factors?.unresolved_hazards || 0} open hazards
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Protective Actions</p>
                      <p className="font-medium">
                        {riskScore.factors?.protective_factors?.inspections_completed || 0} inspections,
                        {riskScore.factors?.protective_factors?.training_completed || 0} training
                      </p>
                    </div>
                  </div>
                </div>
                <div className="text-center ml-6">
                  <div className={`text-5xl font-bold p-4 rounded-full ${getRiskColor(riskScore.risk_level)}`}>
                    {riskScore.risk_score}
                  </div>
                  <Badge className={`mt-2 ${getRiskColor(riskScore.risk_level)}`}>{riskScore.risk_level} risk</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Safety Culture Index */}
        {cultureIndex && (
          <Card className={`border-2 ${cultureIndex.grade === 'A' ? 'border-green-300' : cultureIndex.grade === 'B' ? 'border-blue-300' : 'border-yellow-300'}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Award className="w-5 h-5 text-purple-600" />
                    <h3 className="text-lg font-semibold">Safety Culture Index</h3>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">{cultureIndex.level} safety culture</p>
                  <div className="space-y-2">
                    {Object.entries(cultureIndex.dimensions || {}).slice(0, 3).map(([key, dim]) => (
                      <div key={key} className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 w-24">{dim.label}</span>
                        <Progress value={dim.score} className="h-2 flex-1" />
                        <span className="text-xs font-medium w-8">{dim.score}%</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="text-center ml-6">
                  <div className={`text-5xl font-bold p-4 rounded-full ${getGradeColor(cultureIndex.grade)}`}>
                    {cultureIndex.grade}
                  </div>
                  <p className="mt-2 text-lg font-semibold">{cultureIndex.overall_score}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Key Metrics */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{dashboard.metrics.total_incidents}</p>
                <p className="text-xs text-gray-600">Incidents</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Activity className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{dashboard.metrics.near_misses}</p>
                <p className="text-xs text-gray-600">Near Misses</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Target className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{dashboard.metrics.hazards_reported}</p>
                <p className="text-xs text-gray-600">Hazards</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Users className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{dashboard.metrics.training_completed}</p>
                <p className="text-xs text-gray-600">Training</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{dashboard.metrics.inspections_completed}</p>
                <p className="text-xs text-gray-600">Inspections</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Shield className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{dashboard.metrics.ptps_completed}</p>
                <p className="text-xs text-gray-600">PTPs</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Monthly Trends Chart */}
      {monthlyTrends.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" /> Monthly Safety Trends
            </CardTitle>
            <CardDescription>Safety events over the past 6 months</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-gray-500 mb-2">Incidents</p>
                <SimpleBarChart data={monthlyTrends} dataKey="incidents" color="bg-red-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-2">Near Misses</p>
                <SimpleBarChart data={monthlyTrends} dataKey="near_misses" color="bg-orange-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-2">Training Completed</p>
                <SimpleBarChart data={monthlyTrends} dataKey="training_completed" color="bg-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Compliance Summary */}
      {compliance && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Clipboard className="w-5 h-5" /> Compliance Summary
                </CardTitle>
                <CardDescription>Overall compliance status across safety categories</CardDescription>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-blue-600">{compliance.overall_compliance}%</p>
                <p className="text-sm text-gray-500">Overall Compliance</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {compliance.compliance_items?.map((item, idx) => (
                <Card key={idx} className={`border ${getComplianceColor(item.status)}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">{item.category}</span>
                      {item.status === 'compliant' ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : item.status === 'warning' ? (
                        <AlertCircle className="w-5 h-5 text-yellow-600" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                      )}
                    </div>
                    <p className="text-2xl font-bold">{item.compliance_rate}%</p>
                    <p className="text-xs text-gray-500">{item.non_compliant} of {item.total} non-compliant</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPIs */}
      {kpis.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" /> Key Performance Indicators
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {kpis.map((kpi, idx) => {
                const isGood = getKPIStatus(kpi);
                return (
                  <Card key={idx} className={isGood ? 'border-green-200' : 'border-red-200'}>
                    <CardContent className="p-4">
                      <p className="text-sm text-gray-500 mb-1">{kpi.name}</p>
                      <div className="flex items-center gap-2">
                        <p className="text-2xl font-bold">{kpi.value}</p>
                        <span className="text-sm text-gray-400">{kpi.unit}</span>
                        {isGood ? (
                          <ThumbsUp className="w-4 h-4 text-green-500" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-red-500" />
                        )}
                      </div>
                      <p className="text-xs text-gray-400">Target: {kpi.target} {kpi.unit}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="leading" className="w-full">
        <TabsList>
          <TabsTrigger value="leading">Leading Indicators</TabsTrigger>
          <TabsTrigger value="lagging">Lagging Indicators</TabsTrigger>
          <TabsTrigger value="departments">Department Comparison</TabsTrigger>
          <TabsTrigger value="hazards">Hazard Analysis</TabsTrigger>
        </TabsList>

        <TabsContent value="leading" className="space-y-4">
          {leadingIndicators && (
            <>
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Leading Indicator Score</CardTitle>
                    <div className="text-3xl font-bold text-blue-600">{leadingIndicators.overall_score}%</div>
                  </div>
                  <CardDescription>Proactive safety measures (higher is better)</CardDescription>
                </CardHeader>
                <CardContent>
                  <Progress value={leadingIndicators.overall_score} className="h-3" />
                </CardContent>
              </Card>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {leadingIndicators.indicators?.map((ind, idx) => (
                  <Card key={idx}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{ind.name}</span>
                        <span className="text-lg font-bold">{ind.value} / {ind.target}</span>
                      </div>
                      <Progress value={Math.min((ind.value / ind.target) * 100, 100)} className={`h-2 ${getScoreColor(ind.value, ind.target)}`} />
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="lagging" className="space-y-4">
          {laggingIndicators && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {laggingIndicators.indicators?.map((ind, idx) => (
                <Card key={idx}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{ind.name}</p>
                        <p className="text-xs text-gray-500">Benchmark: {ind.benchmark}</p>
                      </div>
                      <div className="text-right">
                        <p className={`text-2xl font-bold ${ind.value > ind.benchmark ? 'text-red-600' : 'text-green-600'}`}>
                          {ind.value}
                        </p>
                        {ind.value > ind.benchmark ? (
                          <TrendingUp className="w-4 h-4 text-red-500 inline" />
                        ) : (
                          <TrendingDown className="w-4 h-4 text-green-500 inline" />
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="departments" className="space-y-4">
          <div className="space-y-3">
            {departmentComparison.map((dept, idx) => (
              <Card key={idx} className={idx === 0 ? 'border-green-300 bg-green-50' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                        #{idx + 1}
                      </span>
                      <div>
                        <p className="font-medium">{dept.department}</p>
                        <p className="text-sm text-gray-500">
                          {dept.incidents} incidents • {dept.near_misses} near misses • {dept.jhas_completed} JHAs
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-blue-600">{dept.safety_score}</p>
                      <p className="text-xs text-gray-500">Safety Score</p>
                    </div>
                  </div>
                  <Progress value={dept.safety_score} className="h-2 mt-3" />
                </CardContent>
              </Card>
            ))}
            {departmentComparison.length === 0 && (
              <Card>
                <CardContent className="text-center py-8">
                  <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No department data available</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="hazards" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {hazardCategories.map((cat, idx) => (
              <Card key={idx}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium capitalize">{cat.category?.replace('_', ' ')}</span>
                    <Badge>{cat.count} total</Badge>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {cat.critical > 0 && <Badge className="bg-red-100 text-red-800">Critical: {cat.critical}</Badge>}
                    {cat.high > 0 && <Badge className="bg-orange-100 text-orange-800">High: {cat.high}</Badge>}
                    {cat.medium > 0 && <Badge className="bg-yellow-100 text-yellow-800">Medium: {cat.medium}</Badge>}
                    {cat.low > 0 && <Badge className="bg-green-100 text-green-800">Low: {cat.low}</Badge>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {hazardCategories.length === 0 && (
            <Card>
              <CardContent className="text-center py-8">
                <PieChart className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No hazard data available for this period</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SafetyAnalyticsPage;
